import React, { useState } from 'react';
import { Formik, Form, useField } from 'formik';
import * as Yup from 'yup';
import { Box, Button, Flex, Text } from '@radix-ui/themes';
import { FaInfoCircle, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import axiosInstance from '../../config/axiosConfig';
import loginLogo from '../../assets/Luka-Healthcare.png';
import signImage from '../../assets/sign-image.svg';
import logoIMCAS from '../../assets/imacs_logo.svg';

const forgotPasswordSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
});

const ForgotPassword = ({ onBack }) => {
  const [status, setStatus] = useState('form'); // 'form', 'loading', 'success', 'error'
  const [message, setMessage] = useState('');

  const handleSubmit = async (values, { setSubmitting }) => {
    setStatus('loading');
    
    try {
      const response = await axiosInstance.post('/Account/reset-password', null, {
        params: { email: values.email }
      });

      if (response.data.result) {
        setStatus('success');
        setMessage('Password reset email sent successfully! Please check your email for further instructions.');
      } else {
        setStatus('error');
        setMessage(response.data.errorResponse?.message || 'Failed to send reset email. Please check your email address and try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage(error?.response?.data?.errorResponse?.message || 'Something went wrong. Please check your email address or try again later.');
    }
    
    setSubmitting(false);
  };

  const InputField = ({ label, ...props }) => {
    const [field, meta] = useField(props);
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <input
          {...field}
          {...props}
          className={`mt-1 block w-full px-4 py-3 border rounded-md shadow-sm ${
            meta.touched && meta.error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
          }`}
        />
        {meta.touched && meta.error && (
          <div className="mt-2 text-sm text-red-500">{meta.error}</div>
        )}
      </div>
    );
  };

  if (status === 'success') {
    return (
      <Flex direction={'row'} className='min-h-screen w-screen'>
        {/* Left decorative panel — hidden on mobile, visible on lg+ */}
        <Box className='hidden lg:flex h-screen bg-[#ecf2f1] w-[41.5%] sticky top-0'>
          <Flex gap={'6'} direction={'column'} align={'center'} className='h-full w-full'>
            <img className='w-[70%] my-24' src={loginLogo} alt="" />
            <img className='w-[88%] mt-0' src={signImage} alt="" />
          </Flex>
        </Box>
        {/* Right panel — full width on mobile */}
        <Box className='w-full lg:w-[59.5%]'>
          <Flex direction={'column'} align={'center'} justify={'center'} className='min-h-screen py-10 px-6'>
            <div className="w-full max-w-[450px] text-center">
              <img width={'300px'} className='mb-8 mx-auto' src={logoIMCAS} alt="" />
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <FaCheckCircle className="text-green-500 text-4xl mx-auto mb-4" />
                <Text size="6" weight="bold" className="text-green-700 mb-2 block">
                  Email Sent Successfully!
                </Text>
                <Text size="3" className="text-green-600 mb-4 block">
                  {message}
                </Text>
                <Text size="2" className="text-gray-600">
                  Didn't receive the email? Check your spam folder or contact support.
                </Text>
              </div>

              <button
                onClick={onBack}
                className="w-full py-3 px-4 bg-[#0b5a97] text-white font-medium rounded-sm shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2"
              >
                <FaArrowLeft />
                Back to Login
              </button>
            </div>
          </Flex>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction={'row'} className='min-h-screen w-screen'>
      {/* Left decorative panel — hidden on mobile, visible on lg+ */}
      <Box className='hidden lg:flex h-screen bg-[#ecf2f1] w-[41.5%] sticky top-0'>
        <Flex gap={'6'} direction={'column'} align={'center'} className='h-full w-full'>
          <img className='w-[70%] my-24' src={loginLogo} alt="" />
          <img className='w-[88%] mt-0' src={signImage} alt="" />
        </Flex>
      </Box>
      {/* Right panel — full width on mobile */}
      <Box className='w-full lg:w-[59.5%]'>
        <Flex direction={'column'} align={'center'} justify={'center'} className='min-h-screen py-10 px-6'>
          <div className="w-full max-w-[450px]">
            <div className="flex flex-col mb-6">
              <img width={'300px'} className='mb-6' src={logoIMCAS} alt="" />
              <Text size="8" className="mb-2">Forgot Password?</Text>
              <Text size="2" className="text-gray-600">
                Enter your email address and we'll send you a link to reset your password.
              </Text>
            </div>

            <Formik
              initialValues={{ email: '' }}
              validationSchema={forgotPasswordSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form>
                  <InputField
                    label="Email Address"
                    name="email"
                    type="email"
                    placeholder="Enter your email address"
                  />

                  {status === 'error' && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 flex items-center">
                      <FaInfoCircle className="mr-2 flex-shrink-0" />
                      <span>{message}</span>
                    </div>
                  )}

                  <div className="space-y-3">
                    <button
                      type="submit"
                      className="w-full py-3 px-4 bg-[#0b5a97] text-white font-medium rounded-sm shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting || status === 'loading'}
                    >
                      {status === 'loading' ? 'Sending Reset Link...' : 'Send Reset Link'}
                    </button>

                    <button
                      type="button"
                      onClick={onBack}
                      className="w-full py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-sm shadow hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2"
                    >
                      <FaArrowLeft />
                      Back to Login
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Flex>
      </Box>
    </Flex>
  );
};

export default ForgotPassword;
